import { Button } from "@/components/ui/button";
import type {
  Document,
  DocumentCategory,
  DocumentType,
} from "@/schemas/document";
import { motion } from "framer-motion";
import { Check, FileText, Plus, X } from "lucide-react";

interface RequiredDocument {
  id: string;
  title: string;
  description: string;
  required: boolean;
  category: DocumentCategory;
  type: DocumentType;
  expectedFrom?: string;
}

interface DocumentItemProps {
  doc: RequiredDocument;
  selectedDocument?: Document;
  onSelect: () => void;
  onDelete: () => void;
}

export function DocumentItem({
  doc,
  selectedDocument,
  onSelect,
  onDelete,
}: DocumentItemProps) {
  return (
    <motion.div
      key={doc.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative rounded-lg border bg-card p-4 shadow-sm"
    >
      <div className="flex flex-col md:flex-row items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex flex-col-reverse md:flex-row md:items-center gap-2">
            <h4 className="font-semibold">{doc.title}</h4>
            <div className="flex flex-col md:flex-row md:items-center gap-2">
              {doc.required && (
                <div className="rounded-full w-fit bg-red-100 px-2 py-0.5 text-xs text-red-700">
                  Required
                </div>
              )}
              {doc.expectedFrom && (
                <div className="rounded-full w-fit bg-yellow-100 px-2 py-0.5 text-xs text-yellow-700">
                  Expected from {doc.expectedFrom}
                </div>
              )}
            </div>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {doc.description}
          </p>
        </div>

        <div className="ml-auto">
          {selectedDocument ? (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1">
                <Check className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">Added</span>
              </div>
              <Button variant="ghost" size="sm" onClick={onSelect}>
                Change
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={onSelect}
            >
              <Plus className="h-4 w-4" />
              Add Document
            </Button>
          )}
        </div>
      </div>

      {selectedDocument && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mt-3 flex items-center gap-3 rounded-md bg-muted p-3"
        >
          <FileText className="h-4 w-4 text-muted-foreground" />
          <div className="flex-1">
            <p className="text-sm font-semibold">{selectedDocument.name}</p>
            <p className="text-xs text-muted-foreground">
              Added {new Date(selectedDocument.created_at).toLocaleDateString()}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onDelete}>
            <X className="h-4 w-4 text-muted-foreground" />
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
}
